import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Store } from 'lucide-react';
import { shopInviteAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import ProductTicker from '@/components/ProductTicker';
import { useToast } from '@/hooks/use-toast';

/**
 * Which of my products this shop gets. Ticked products are in the shop's
 * stock (the shop sets its own quantity and price); unticking removes them.
 */
export default function ShopProductsPage() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [shop, setShop] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saved, setSaved] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    shopInviteAPI.shopProducts(shopId).then((res) => {
      setShop(res.data.shop);
      setItems(res.data.items);
      const ticked = new Set(res.data.items.filter((i) => i.assigned).map((i) => i.product_id));
      setSelected(ticked);
      setSaved(ticked);
    }).catch(() => setMissing(true)).finally(() => setLoading(false));
  }, [shopId]);

  const changed = selected.size !== saved.size || [...selected].some((id) => !saved.has(id));
  const save = async () => {
    setSaving(true);
    try {
      const res = await shopInviteAPI.setShopProducts(shopId, [...selected]);
      setSaved(new Set(selected));
      toast({ title: t('shopProducts.saved', { added: res.data.added, removed: res.data.removed }) });
    } catch {
      toast({ title: t('shopProducts.saveFailed'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-farm-primary" /></div>;
  }
  return (
    <div className="p-4 md:p-6 animate-fadeIn pb-28">
      <button type="button" onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-gray-700 font-semibold h-10 -ml-1 mb-2">
        <ArrowLeft className="w-5 h-5" /> {t('wizard.back')}
      </button>
      {missing ? (
        <p className="text-gray-700" data-testid="shop-products-missing">{t('shopProducts.notYours')}</p>
      ) : (
        <>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Store className="w-6 h-6 text-farm-primary" /> {shop?.name}
          </h1>
          <p className="text-muted-foreground mb-4">{t('shopProducts.subtitle')}</p>
          <ProductTicker items={items} selected={selected} onChange={setSelected} maxHeight="max-h-none" />
          <div className="fixed left-0 right-0 bottom-20 px-4 z-30">
            <div className="max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto">
              <Button className="w-full h-14 text-base font-bold bg-farm-primary hover:bg-farm-primary-dark shadow-lg"
                      disabled={!changed || saving} onClick={save} data-testid="shop-products-save">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : t('shopProducts.save', { n: selected.size })}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
